/**
 * Zod schema for number and fraction fields.
 * Messages: Required, Not a number, Out of range, Whole numbers only.
 */

import { z } from 'zod';
import type { FieldSchemaConfig } from '../types';

export function getNumberSchema(col: FieldSchemaConfig): z.ZodTypeAny {
  const required = 'required' in col && col.required === true;
  const min = col.min;
  const max = col.max;
  const minExclusive = col.minExclusive === true;
  const allowedDecimals = col.allowedDecimals;

  const numLike = z
    .union([z.number(), z.string(), z.null(), z.undefined()])
    .transform((v): number | null => {
      if (v === null || v === undefined || v === '') return null;
      if (typeof v === 'number') return Number.isNaN(v) ? null : v;
      const n = Number(v);
      return Number.isNaN(n) ? null : n;
    });

  let schema: z.ZodTypeAny = numLike;

  if (required) {
    schema = schema.refine(
      (v: number | null) => v !== null && v !== undefined && !Number.isNaN(v),
      'A number is required.'
    ) as z.ZodTypeAny;
  }

  schema = schema.refine(
    (v: number | null) => v === null || (Number.isFinite(v) && !Number.isNaN(v)),
    'Enter a valid number.'
  ) as z.ZodTypeAny;

  const minCheck = minExclusive
    ? (v: number) => v > (min as number)
    : (v: number) => v >= (min as number);

  if (min != null && max != null) {
    schema = schema.refine(
      (v: number | null) => v === null || (minCheck(v) && v <= max),
      minExclusive
        ? `Value must be greater than ${min} and at most ${max}.`
        : `Value must be between ${min} and ${max}.`
    ) as z.ZodTypeAny;
  } else if (min != null) {
    schema = schema.refine(
      (v: number | null) => v === null || minCheck(v),
      minExclusive
        ? `Value must be greater than ${min}.`
        : `Value must be between ${min} and ${max ?? min}.`
    ) as z.ZodTypeAny;
  } else if (max != null) {
    schema = schema.refine(
      (v: number | null) => v === null || v <= max,
      `Value must be between ${min ?? max} and ${max}.`
    ) as z.ZodTypeAny;
  }

  if (allowedDecimals === 0) {
    schema = schema.refine(
      (v: number | null) => v === null || Number.isInteger(v),
      'Whole numbers only.'
    ) as z.ZodTypeAny;
  } else if (allowedDecimals != null && allowedDecimals > 0) {
    schema = schema.refine(
      (v: number | null) =>
        v === null ||
        (() => {
          const str = String(v);
          const dot = str.indexOf('.');
          if (dot === -1) return true;
          return str.length - dot - 1 <= allowedDecimals;
        })(),
      { message: `At most ${allowedDecimals} decimal place(s) allowed` }
    ) as z.ZodTypeAny;
  }

  return schema;
}
